import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PlugsConnected, Copy, Trash, Plus } from "@phosphor-icons/react";
import { useApi, errMsg, nzDateTime, API } from "@/lib/api";
import { Btn, Pill, Modal, useLoad } from "@/components/app/common";

const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success("Copied"), () => toast.error("Copy failed — select and copy manually"));
const CHANGED = "fo-connector-change";

export function ChatGPTBadge() {
  const api = useApi();
  const [links, reload] = useLoad(() => api.get("/connector/tokens"), []);
  useEffect(() => { window.addEventListener(CHANGED, reload); return () => window.removeEventListener(CHANGED, reload); }, [reload]);
  if (!links?.length) return null;
  const used = links.map((l) => l.last_used_at).filter(Boolean).sort().pop();
  return (
    <span className="inline-flex items-center gap-2 h-8 px-3 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold" title={used ? `Last used ${nzDateTime(used)}` : "Link active, not used yet"} data-testid="chatgpt-connected-badge">
      <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-600" /></span>
      <span className="hidden md:inline">ChatGPT connected</span><PlugsConnected size={14} className="md:hidden" />
    </span>
  );
}

function NewLink({ url, onClose }) {
  return (
    <Modal open title="Your ChatGPT connector link" onClose={onClose} testid="connector-new-modal">
      <p className="text-sm text-slate-600 mb-3">Copy this now — it is shown only once. Anyone with this link can see and change anything in Farmops that you can, so keep it private.</p>
      <div className="flex gap-2"><input readOnly className="field-input font-mono text-xs" value={url} data-testid="connector-url-input" onFocus={(e) => e.target.select()} />
        <Btn onClick={() => copy(url)} data-testid="connector-copy-button"><Copy size={16} />Copy</Btn></div>
      <ol className="text-sm text-slate-700 list-decimal pl-5 mt-4 space-y-1">
        <li>In ChatGPT open Settings → Apps &amp; Connectors → Advanced, and turn on Developer mode.</li>
        <li>Choose Create (or Add connector), name it “Farm Operations” and paste this link as the MCP server URL.</li>
        <li>Set Authentication to “No authentication” — the link itself is your key.</li>
        <li>In a chat, add Farm Operations from the tools menu and ask e.g. “Show my latest inspections”.</li>
      </ol>
    </Modal>
  );
}

export function ChatGPTConnector() {
  const api = useApi();
  const [links, reload] = useLoad(() => api.get("/connector/tokens"), []);
  const [fresh, setFresh] = useState(null);
  const create = async () => { try { const r = await api.post("/connector/tokens", { label: "ChatGPT" }); setFresh(`${API}/mcp/${r.token}`); reload(); window.dispatchEvent(new Event(CHANGED)); } catch (e) { toast.error(errMsg(e)); } };
  const revoke = async (id) => { if (!window.confirm("Turn off this link? ChatGPT will stop reaching Farm Operations with it.")) return; try { await api.del(`/connector/tokens/${id}`); reload(); window.dispatchEvent(new Event(CHANGED)); toast.success("Link turned off"); } catch (e) { toast.error(errMsg(e)); } };
  const active = links && links.length > 0;
  return (
    <div className="panel p-4 mb-6" data-testid="farm-ops-status">
      <div className="flex items-start gap-3 flex-wrap">
        <PlugsConnected size={22} className="text-slate-500 mt-0.5" />
        <div className="flex-1 min-w-[220px]">
          <p className="text-sm font-semibold">ChatGPT connection: <Pill tone={active ? "green" : "orange"} testid="connector-status-pill">{active ? "Link active" : "Not connected"}</Pill></p>
          <p className="text-xs text-slate-600 mt-1">Let ChatGPT do anything in Farmops that you can: tasks, spending requests, feeding, livestock, finances, machinery, inspections, compliance, reporting, policy checks, grazing, settings and file uploads (share a photo of a handwritten sheet and it reads it, checks with you, then saves). It always asks before deleting or archiving, and acts with your access — nothing more.</p>
        </div>
        <Btn variant="outline" onClick={create} data-testid="connector-create-button"><Plus size={16} />{active ? "New link" : "Connect ChatGPT"}</Btn>
      </div>
      {active && <ul className="mt-3 divide-y divide-slate-100 text-xs" data-testid="connector-link-list">{links.map((l) => (
        <li key={l.id} className="flex items-center justify-between py-2 gap-2" data-testid={`connector-link-${l.id}`}>
          <span>{l.label} · ends …{l.hint} · created {nzDateTime(l.created_at)} · {l.last_used_at ? `last used ${nzDateTime(l.last_used_at)}` : "not used yet"}</span>
          <button onClick={() => revoke(l.id)} className="text-red-600 inline-flex items-center gap-1" data-testid={`connector-revoke-${l.id}`}><Trash size={14} />Turn off</button>
        </li>))}</ul>}
      {fresh && <NewLink url={fresh} onClose={() => setFresh(null)} />}
    </div>
  );
}
